import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useCartStore, useAuthStore } from '../store';
import api from '../utils/api';
import { toast } from 'react-toastify';
import './Checkout.css';

const COIN_COLORS = { BTC:'#f7931a', ETH:'#627eea', USDC:'#2775ca', LTC:'#bfbbbb', BNB:'#f3ba2f' };

export default function Checkout() {
  const navigate   = useNavigate();
  const { items, clearCart } = useCartStore();
  const { user }   = useAuthStore();

  const [step, setStep]         = useState(1); // 1=address, 2=coin select, 3=pay
  const [coins, setCoins]       = useState([]);
  const [selectedCoin, setCoin] = useState(null);
  const [payment, setPayment]   = useState(null);
  const [orderId, setOrderId]   = useState(null);
  const [txHash, setTxHash]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [timeLeft, setTimeLeft] = useState(1800); // 30 min
  const [address, setAddress]   = useState({
    firstName: user?.firstName||'', lastName: user?.lastName||'',
    address:'', city:'', state:'', zip:'', country:'US', phone:''
  });

  const subtotal = items.reduce((s,i)=>s+i.price*i.quantity,0);
  const shipping  = subtotal>=150?0:9.99;
  const total     = subtotal+shipping;

  useEffect(() => {
    api.get('/payments/crypto/rates').then(r=>setCoins(r.data.coins)).catch(()=>{});
  }, []);

  useEffect(() => {
    if (step !== 3 || !payment) return;
    const t = setInterval(()=>setTimeLeft(s=>{ if(s<=1){ clearInterval(t); return 0; } return s-1; }),1000);
    return ()=>clearInterval(t);
  }, [step, payment]);

  const handleAddr = e => setAddress(a=>({...a,[e.target.name]:e.target.value}));

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    if (!selectedCoin) { toast.error('Please select a cryptocurrency'); return; }
    setLoading(true);
    try {
      // Create order
      const orderRes = await api.post('/orders', {
        items, shippingAddress:address,
        paymentMethod:`crypto_${selectedCoin.coin}`,
        cryptoCoin:selectedCoin.coin, notes:''
      });
      const oid = orderRes.data.order.id;
      setOrderId(oid);

      // Create crypto payment
      const payRes = await api.post('/payments/crypto/create', { orderId:oid, coin:selectedCoin.coin, amountUsd:total });
      setPayment(payRes.data.payment);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.message||'Failed to create order');
    } finally { setLoading(false); }
  };

  const handleConfirm = async () => {
    if (!txHash.trim()) { toast.error('Please enter your transaction hash'); return; }
    setLoading(true);
    try {
      await api.post('/payments/crypto/confirm', { paymentId:payment.id, txHash });
      await clearCart();
      toast.success('Payment submitted! Order confirmed.');
      navigate('/order-success');
    } catch (err) {
      toast.error(err.response?.data?.message||'Confirmation failed');
    } finally { setLoading(false); }
  };

  const fmt = (s) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

  if (items.length === 0) return (
    <div style={{minHeight:'60vh',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div className="card" style={{padding:'2.5rem',textAlign:'center'}}>
        <p style={{fontSize:'2rem'}}>🛒</p>
        <h2>Your cart is empty</h2>
        <a href="/shop" className="btn btn-primary" style={{marginTop:'1rem',display:'inline-block'}}>Browse Products</a>
      </div>
    </div>
  );

  return (
    <div className="container" style={{padding:'2.5rem 1.5rem'}}>
      <h1 style={{fontSize:'1.75rem',fontWeight:800,marginBottom:'0.5rem'}}>Checkout</h1>

      {/* Steps */}
      <div className="checkout-steps">
        {['Shipping','Select Coin','Send Payment'].map((s,i)=>(
          <div key={s} className={`checkout-step${step===i+1?' active':step>i+1?' done':''}`}>
            <div className="step-num">{step>i+1?'✓':i+1}</div>
            <span>{s}</span>
          </div>
        ))}
      </div>

      <div className="checkout-layout">
        {/* LEFT */}
        <div>
          {/* Step 1: Address */}
          {step===1 && (
            <form onSubmit={e=>{e.preventDefault();if(!selectedCoin){toast.error('Select a coin first');return;}setStep(2);}}>
              <div className="checkout-section card">
                <h3>Shipping Information</h3>
                <div className="auth-row">
                  <div className="form-group"><label className="form-label">First Name</label><input name="firstName" className="form-input" value={address.firstName} onChange={handleAddr} required /></div>
                  <div className="form-group"><label className="form-label">Last Name</label><input name="lastName" className="form-input" value={address.lastName} onChange={handleAddr} required /></div>
                </div>
                <div className="form-group"><label className="form-label">Street Address</label><input name="address" className="form-input" value={address.address} onChange={handleAddr} required /></div>
                <div className="addr-row3">
                  <div className="form-group"><label className="form-label">City</label><input name="city" className="form-input" value={address.city} onChange={handleAddr} required /></div>
                  <div className="form-group"><label className="form-label">State</label><input name="state" className="form-input" value={address.state} onChange={handleAddr} required /></div>
                  <div className="form-group"><label className="form-label">ZIP</label><input name="zip" className="form-input" value={address.zip} onChange={handleAddr} required /></div>
                </div>
                <div className="form-group"><label className="form-label">Phone</label><input name="phone" type="tel" className="form-input" value={address.phone} onChange={handleAddr} /></div>
              </div>

              <div className="checkout-section card" style={{marginTop:'1.5rem'}}>
                <h3>Select Cryptocurrency</h3>
                <p style={{fontSize:'0.875rem',color:'var(--gray-500)',marginBottom:'1.25rem'}}>Choose which coin you will pay with. Live rates from CoinGecko.</p>
                <div className="coin-grid">
                  {coins.map(c=>(
                    <button key={c.coin} type="button"
                      className={`coin-option${selectedCoin?.coin===c.coin?' selected':''}`}
                      onClick={()=>setCoin(c)}
                      style={{'--coin-color':COIN_COLORS[c.coin]||'#666'}}>
                      <span className="coin-logo">{c.logo}</span>
                      <div>
                        <strong>{c.coin}</strong>
                        <span>{c.name}</span>
                        <small>1 {c.coin} = ${c.usdRate?.toLocaleString()}</small>
                      </div>
                      {selectedCoin?.coin===c.coin && <span className="coin-check">✓</span>}
                    </button>
                  ))}
                </div>
                {selectedCoin && (
                  <div className="coin-estimate">
                    You will pay approximately <strong>{(total/selectedCoin.usdRate).toFixed(8)} {selectedCoin.coin}</strong> (${total.toFixed(2)} USD)
                  </div>
                )}
              </div>

              <button type="submit" className="btn btn-primary btn-lg" style={{width:'100%',marginTop:'1.5rem'}} disabled={!selectedCoin||loading}>
                Continue to Payment →
              </button>
            </form>
          )}

          {/* Step 2: Review & confirm */}
          {step===2 && (
            <div>
              <div className="checkout-section card">
                <h3>Review & Confirm</h3>
                <div style={{background:'var(--gray-50)',borderRadius:'var(--radius)',padding:'1rem',marginBottom:'1.25rem'}}>
                  <p style={{fontWeight:600,marginBottom:'0.375rem'}}>Paying with: <span style={{color:COIN_COLORS[selectedCoin?.coin]}}>{selectedCoin?.coin} — {selectedCoin?.name}</span></p>
                  <p style={{fontSize:'0.875rem',color:'var(--gray-500)'}}>Rate: 1 {selectedCoin?.coin} = ${selectedCoin?.usdRate?.toLocaleString()} USD</p>
                  <p style={{fontSize:'1.125rem',fontWeight:700,color:'var(--green-600)',marginTop:'0.5rem'}}>
                    ≈ {(total/selectedCoin?.usdRate).toFixed(8)} {selectedCoin?.coin}
                  </p>
                </div>
                <div style={{marginBottom:'1rem'}}>
                  <p style={{fontSize:'0.875rem',color:'var(--gray-600)'}}><strong>Ship to:</strong> {address.firstName} {address.lastName}, {address.address}, {address.city} {address.state} {address.zip}</p>
                </div>
                <div className="checkout-disclaimer">
                  ⚠️ Crypto payments are <strong>non-refundable</strong> once confirmed on-chain. Make sure all details are correct before proceeding.
                </div>
              </div>
              <div style={{display:'flex',gap:'1rem',marginTop:'1.5rem'}}>
                <button className="btn btn-secondary" onClick={()=>setStep(1)}>← Back</button>
                <button className="btn btn-primary btn-lg" style={{flex:1}} onClick={handlePlaceOrder} disabled={loading}>
                  {loading?<span className="spinner"/>:'Place Order & Get Payment Address →'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Send payment */}
          {step===3 && payment && (
            <div className="checkout-section card">
              <div className="payment-header">
                <h3>Send {payment.coin} Payment</h3>
                <div className={`payment-timer${timeLeft<300?' urgent':''}`}>⏱ {fmt(timeLeft)}</div>
              </div>
              <p style={{color:'var(--gray-500)',fontSize:'0.875rem',marginBottom:'1.5rem'}}>
                Send <strong>{payment.amountCrypto} {payment.coin}</strong> (≈${payment.amountUsd?.toFixed(2)}) to the address below. The payment window expires in {fmt(timeLeft)}.
              </p>

              <div className="payment-qr">
                <QRCodeSVG value={payment.qrData} size={180} level="H" includeMargin />
              </div>

              <div className="payment-address-box">
                <label>Send exactly to this address:</label>
                <div className="payment-address">
                  <code>{payment.address}</code>
                  <button onClick={()=>{navigator.clipboard.writeText(payment.address);toast.success('Address copied!');}}>📋 Copy</button>
                </div>
              </div>

              <div className="payment-amount-box">
                <span>Amount:</span>
                <strong>{payment.amountCrypto} {payment.coin}</strong>
                <button onClick={()=>{navigator.clipboard.writeText(payment.amountCrypto);toast.success('Amount copied!');}}>📋</button>
              </div>

              <div className="form-group" style={{marginTop:'1.5rem'}}>
                <label className="form-label">Transaction Hash (after sending)</label>
                <input className="form-input" value={txHash} onChange={e=>setTxHash(e.target.value)} placeholder={`e.g. 0xabc123... or txid...`} style={{fontFamily:'monospace',fontSize:'0.875rem'}} />
                <p style={{fontSize:'0.8125rem',color:'var(--gray-400)',marginTop:'0.375rem'}}>
                  Find this in your wallet after sending. Paste it here to confirm your order.
                </p>
              </div>

              <button className="btn btn-primary btn-lg" style={{width:'100%',marginTop:'0.5rem'}} onClick={handleConfirm} disabled={loading||!txHash.trim()}>
                {loading?<span className="spinner"/>:'Confirm Payment Sent ✓'}
              </button>

              <div className="payment-note">
                💡 Your order will be processed after 1 blockchain confirmation. You'll receive an email confirmation once verified.
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Order summary */}
        <div className="checkout-summary card">
          <h3>Order Summary</h3>
          <div className="checkout-items">
            {items.map(i=>(
              <div key={i.key} className="checkout-item">
                <span>{i.name} × {i.quantity}</span>
                <strong>${(i.price*i.quantity).toFixed(2)}</strong>
              </div>
            ))}
          </div>
          <hr className="divider"/>
          <div className="summary-row"><span>Subtotal</span><strong>${subtotal.toFixed(2)}</strong></div>
          <div className="summary-row"><span>Shipping</span><strong>{shipping===0?'FREE':`$${shipping.toFixed(2)}`}</strong></div>
          <hr className="divider"/>
          <div className="summary-row summary-total"><span>Total (USD)</span><strong>${total.toFixed(2)}</strong></div>
          {selectedCoin && (
            <div className="summary-crypto" style={{color:COIN_COLORS[selectedCoin.coin]}}>
              ≈ {(total/selectedCoin.usdRate).toFixed(8)} {selectedCoin.coin}
            </div>
          )}
          <div className="summary-accepted">
            <p style={{fontSize:'0.75rem',color:'var(--gray-400)',textAlign:'center',marginTop:'1.25rem'}}>Accepted Cryptocurrencies:</p>
            <div style={{display:'flex',justifyContent:'center',gap:'0.75rem',marginTop:'0.5rem',fontSize:'1.25rem'}}>
              {['₿','Ξ','$','Ł','B'].map((s,i)=><span key={i} title={['Bitcoin','Ethereum','USDC','Litecoin','BNB'][i]}>{s}</span>)}
            </div>
          </div>
          <div style={{marginTop:'1.25rem',background:'var(--gray-50)',borderRadius:'var(--radius)',padding:'0.875rem',fontSize:'0.8125rem',color:'var(--gray-500)'}}>
            🔒 All transactions are secured by blockchain cryptography. No personal payment data stored.
          </div>
        </div>
      </div>
    </div>
  );
}
